import {Code, ConnectError} from "@connectrpc/connect";
import type {InternalAxiosRequestConfig} from "axios";
import {getConsent} from "consent/consentStore";
import {landingContextForPurchase, type LandingAttribution} from "./landingVisit";
import {rememberLandingErasure} from "./landingErasure";

/**
 * The context still allowed to leave: valid, consented and with an erasure capability on file. Read again right
 * before a request leaves, because consent can be withdrawn while the Firebase token is being resolved.
 */
function consentedContext(captured: LandingAttribution | undefined): LandingAttribution | undefined {
	const context = landingContextForPurchase(captured, getConsent().analytics);
	return context !== undefined && rememberLandingErasure(context) ? context : undefined;
}

/**
 * The consent check of a landing observation (`RecordLandingObservation`): captures the context now, before the
 * Firebase token is read, and returns the function that enforces it. The call passes that function as its
 * `kBeforeSend`, so the check runs again right before every request that leaves, a refresh retry included. An
 * observation without consent is cancelled, never sent without its attribution.
 */
export function landingObservationGate(attribution: unknown): () => void {
	const captured = landingContextForPurchase(attribution, getConsent().analytics);
	return () => {
		if (consentedContext(captured) === undefined) {
			throw new ConnectError("Landing analytics consent withdrawn", Code.Canceled);
		}
	};
}

/**
 * Capture before Firebase's async token read; enforce again immediately before Axios sends. A purchase and its
 * legal acknowledgement continue without analytics, so a withdrawn consent strips the `landingAttribution` from the
 * body, a 401 retry included.
 */
export function landingRequestConsent(config: InternalAxiosRequestConfig): () => void {
	let body: unknown = config.data;
	if (typeof body === "string") {
		try { body = JSON.parse(body); }
		catch { return () => {}; }
	}
	if (body === null || typeof body !== "object" || !("landingAttribution" in body)) return () => {};
	const data = body as Record<string, unknown>;
	const captured = landingContextForPurchase(data.landingAttribution, getConsent().analytics);
	return () => {
		if (consentedContext(captured) !== undefined) return;
		const {landingAttribution: _attribution, ...purchase} = data;
		config.data = purchase;
	};
}
